import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { AnyCommonSynchronizationEnvironmentTypesConstraint } from '../../types/CommonSynchronizationEnvironmentTypes';
import { EntityInfo, ObjectFieldInfo, ObjectFieldInfos } from '../EntityInfo/EntityInfo';
import { DataOfValue } from '../FieldInfoHandler/FieldInfoHandler';

/**
 * A handler for a specific path of an entity info.
 * It allows for an efficient way to iterate over all values of the path.
 */
export class EntityInfoPathHandler<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntity> {
  private readonly preparedPath: Array<PreparedPathElement<TCommonSynchronizationEnvironmentTypes>>;
  constructor({
    entityInfo,
    path
  }: EntityInfoPathHandlerOptions<TCommonSynchronizationEnvironmentTypes, TEntity>) {
    this.preparedPath = this.preparePath({
      fieldInfos: entityInfo.fieldInfos,
      remainingPath: path,
      currentPath: []
    });
  }
  public forEachValue({
    data,
    callback
  }: ForEachValueOptions<TCommonSynchronizationEnvironmentTypes>): void {
    this.some({
      data,
      callback: args => {
        callback(args);
        return false;
      }
    });
  }
  public firstValue({
    data
  }: {
    data: Record<string | number | symbol, any>;
  }): unknown | null {
    let value = null;
    this.some({
      data,
      callback: options => {
        // does object destructuring even work when assigning to existing variables? o.O
        // eslint-disable-next-line @typescript-eslint/prefer-destructuring
        value = options.value;
        return true;
      }
    });
    return value;
  }
  public some({
    data,
    callback
  }: SomeOptions<TCommonSynchronizationEnvironmentTypes>): boolean {
    return this.someRecursive({
      data,
      remainingPath: this.preparedPath,
      callback
    });
  }
  private someRecursive({
    data,
    remainingPath: oldRemainingPath,
    callback
  }: {
    data: any;
    remainingPath: Array<PreparedPathElement<TCommonSynchronizationEnvironmentTypes>>;
    callback: SomeCallback<TCommonSynchronizationEnvironmentTypes>;
  }): boolean {
    const remainingPath = [...oldRemainingPath];
    const pathElement = remainingPath.shift();
    assertNotNullOrUndefined(pathElement, 'no pathElement found, make sure you call with a remainingPath which has a length greater than 0');
    const value = data[pathElement.fieldInfo.name];
    if (value === undefined) {
      return false;
    }
    if (pathElement.isArray) {
      if (Array.isArray(value)) {
        for (const valueItem of value) {
          if (this.callRecursiveSomeOrCallback({
            data,
            value: valueItem,
            pathElement,
            remainingPath,
            callback
          })) {
            return true;
          }
        }
        return false;
      }
    }
    return this.callRecursiveSomeOrCallback({
      data,
      value,
      pathElement,
      remainingPath,
      callback
    });
  }
  private callRecursiveSomeOrCallback({
    data,
    value,
    pathElement,
    remainingPath,
    callback
  }: {
    data: any;
    value: any;
    pathElement: PreparedPathElement<TCommonSynchronizationEnvironmentTypes>;
    remainingPath: Array<PreparedPathElement<TCommonSynchronizationEnvironmentTypes>>;
    callback: SomeCallback<TCommonSynchronizationEnvironmentTypes>;
  }): boolean {
    if (remainingPath.length) {
      if (value == null) {
        return false;
      }
      return this.someRecursive({
        data: value,
        remainingPath,
        callback
      });
    }
    return callback({
      data,
      value,
      fieldInfo: pathElement.fieldInfo
    });
  }
  private preparePath({
    fieldInfos,
    remainingPath: oldRemainingPath,
    currentPath: oldCurrentPath
  }: {
    fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any>;
    remainingPath: PathHandlerPath;
    currentPath: PathHandlerPath;
  }): Array<PreparedPathElement<TCommonSynchronizationEnvironmentTypes>> {
    const remainingPath = [...oldRemainingPath];
    const firstRemainingPathElement = remainingPath.shift();
    assertNotNullOrUndefined(firstRemainingPathElement, 'no path element found, make sure this function is not called with an empty remainingPath');
    const currentPath = [...oldCurrentPath, firstRemainingPathElement];
    const fieldInfo = this.getFieldInfoOfPathElement({
      pathElement: firstRemainingPathElement,
      currentPath,
      fieldInfos
    });
    const isArray = Array.isArray(fieldInfo.innerInfo);
    if (remainingPath.length === 0) {
      return [{
        fieldInfo,
        isArray
      }];
    }
    const {
      innerInfo
    } = fieldInfo;
    assertNotNullOrUndefined(innerInfo, `No innerInfo found in path "${currentPath.join('.')}". Remaining path: "${remainingPath.join('.')}"`);
    const innerFieldInfos = Array.isArray(innerInfo) ? innerInfo[0] : innerInfo;
    return [{
      fieldInfo,
      isArray
    }, ...this.preparePath({
      fieldInfos: innerFieldInfos,
      currentPath,
      remainingPath
    })];
  }
  private getFieldInfoOfPathElement({
    pathElement,
    fieldInfos,
    currentPath
  }: {
    pathElement: string;
    fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any>;
    currentPath: PathHandlerPath;
  }): ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any> {
    // workaround to be able to access the id
    // the id is a special case, since it's not included in the field infos
    if (currentPath.length === 1 && pathElement === 'id') {
      return {
        name: 'id'
      };
    }
    const fieldInfo = (fieldInfos as any)[pathElement] as ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any> | undefined;
    assertNotNullOrUndefined(fieldInfo, `path "${currentPath.join('.')}" has no field info`);
    return fieldInfo;
  }
}
export type EntityInfoPathHandlerOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntity> = {
  entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, any, TEntity>;
  path: PathHandlerPath;
};

/**
 * A path which doesn't contain the Array indices.
 * e.g. ['arrayOfObject', 'name']
 */
export type PathHandlerPath = Array<string>;
export type ForEachValueOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  data: Record<string | number | symbol, any>;
  callback: ForEachValueCallback<TCommonSynchronizationEnvironmentTypes>;
};
export type ForEachValueCallback<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = (options: IterationCallbackOptions<TCommonSynchronizationEnvironmentTypes>) => void;
export type SomeOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  data: Record<string | number | symbol, any>;
  callback: SomeCallback<TCommonSynchronizationEnvironmentTypes>;
};
export type SomeCallback<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = (options: IterationCallbackOptions<TCommonSynchronizationEnvironmentTypes>) => boolean;
export type IterationCallbackOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  data: DataOfValue;
  value: any;
  fieldInfo: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>;
};
type PreparedPathElement<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  fieldInfo: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>;
  isArray: boolean;
};